import { useEffect, useState } from "react";

import { IAdminPatientDetail } from "../../../../@types/admin/AdminPatient";
import { useUpdatePatient } from "../../../../hooks/mutations/admin/patients";
import {
  useCountryOptions,
  useWardOptions,
} from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import { AdminDateField } from "../AdminDateField";
import {
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  ReadOnlyValue,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  WarningNotice,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";
import { WardChip, WardChips } from "./PatientDetailsPanel.styles";

interface IPatientRecordFormProps {
  patient: IAdminPatientDetail;
  /** Rendered in the wards field — part 2's change-ward action (EN-5170). */
  wardAction?: React.ReactNode;
}

interface IRecordFormState {
  sex: string;
  hospitalPatientId: string;
  dob: string;
  ethnicity: string;
  countryId: string;
  wardId: string;
  smoking: string;
  publish: boolean;
  archived: boolean;
  rkdRegistry: boolean;
}

/** `M` / `F`, the single characters the column stores. */
const SEX_OPTIONS = [
  { value: "", label: "Not recorded" },
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

/**
 * The `smoking` column is free text with a short length limit, and Retool
 * offered these three. Kept as a select rather than an input: the values feed
 * reporting, and three spellings of "never" would break it.
 */
const SMOKING_OPTIONS = [
  { value: "", label: "Not recorded" },
  { value: "Never", label: "Never" },
  { value: "Ex", label: "Ex-smoker" },
  { value: "Current", label: "Current" },
];

/** `YYYY-MM-DD` for the date field, from whatever the API sent. */
function toDateValue(value: string | null): string {
  return value ? value.slice(0, 10) : "";
}

function toFormState(patient: IAdminPatientDetail): IRecordFormState {
  return {
    sex: patient.sex ?? "",
    hospitalPatientId: patient.hospitalPatientId ?? "",
    dob: toDateValue(patient.dob),
    ethnicity: patient.ethnicity ?? "",
    countryId: patient.countryId ? String(patient.countryId) : "",
    wardId: patient.wardId ? String(patient.wardId) : "",
    smoking: patient.smoking ?? "",
    publish: patient.publish,
    archived: patient.archived,
    rkdRegistry: patient.rkdRegistry ?? false,
  };
}

/**
 * The patient record half of the detail panel — Retool's `formPatientDetails`
 * (EN-5169).
 *
 * ⚠️ Writes the clinical record. Two fields change what the patient sees:
 * `publish` takes them out of the portal's patient lists, and `archived` ends
 * their monitoring. Both are warned about rather than sitting as bare
 * checkboxes, which is how Retool had them.
 *
 * The PUT sends `wardId`. Retool's body sent `hospitalId:
 * formPatientDetails.data.wardId` — the ward id under the hospital key — which
 * is the `cms_hospital` = ward, `cms_hospitalgroup` = hospital confusion in the
 * wild. `UpdatePatientRequestDto` has no HospitalId at all, so Retool's key was
 * being ignored and the ward moved only because the value happened to be right.
 */
export function PatientRecordForm({
  patient,
  wardAction,
}: IPatientRecordFormProps) {
  const { showToast } = useToast();
  const { updatePatient, isUpdatingPatient } = useUpdatePatient();
  const { countries } = useCountryOptions();
  // The ward select is scoped to the patient's hospital group: a patient cannot
  // be moved to a ward of another hospital from this form (that is what the
  // change-ward action is for, and it re-scopes deliberately).
  const { wards } = useWardOptions(patient.hospitalId);

  const [form, setForm] = useState<IRecordFormState>(() =>
    toFormState(patient)
  );

  useEffect(() => {
    setForm(toFormState(patient));
  }, [patient]);

  const errors = {
    countryId: form.countryId ? undefined : "Country is required.",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const setField = <TKey extends keyof IRecordFormState>(
    key: TKey,
    value: IRecordFormState[TKey]
  ) => setForm((previous) => ({ ...previous, [key]: value }));

  const handleSave = async () => {
    if (hasErrors) return;

    try {
      await updatePatient({
        patientId: Number(patient.id),
        request: {
          id: Number(patient.id),
          // Retool hard-coded order: 1 in its body. The record's own value is
          // sent instead, so saving the form cannot reorder the patient.
          order: patient.order,
          publish: form.publish,
          sex: form.sex || null,
          hospitalPatientId: form.hospitalPatientId.trim() || null,
          dob: form.dob || null,
          ethnicity: form.ethnicity.trim() || null,
          countryId: Number(form.countryId),
          wardId: form.wardId ? Number(form.wardId) : null,
          smoking: form.smoking || null,
          rkdRegistry: form.rkdRegistry,
          archived: form.archived,
        },
      });
      showToast("The patient details were updated successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating the patient details."
        ),
        "warning"
      );
    }
  };

  const willUnpublish = patient.publish && !form.publish;
  const willArchive = !patient.archived && form.archived;

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>Patient details</SectionTitle>
          <SectionCaption>
            Patient ID {patient.id} · created{" "}
            {patient.created ? patient.created.slice(0, 10) : "-"}
          </SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel>Hospital</FilterLabel>
          <ReadOnlyValue>{patient.hospital || "-"}</ReadOnlyValue>
          <FieldHint>
            Moving a patient between hospitals is not done from this form.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-ward">Primary ward</FilterLabel>
          <AdminSelectField
            id="patient-ward"
            value={form.wardId}
            onChange={(e) => setField("wardId", e.target.value)}
          >
            <option value="">Not set</option>
            {wards.map((ward) => (
              <option key={ward.id} value={String(ward.id)}>
                {ward.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-mrn">MRN</FilterLabel>
          <FilterInput
            id="patient-mrn"
            type="text"
            maxLength={70}
            value={form.hospitalPatientId}
            onChange={(e) => setField("hospitalPatientId", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-dob">Date of birth</FilterLabel>
          <AdminDateField
            id="patient-dob"
            value={form.dob}
            onChange={(value) => setField("dob", value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-sex">Sex</FilterLabel>
          <AdminSelectField
            id="patient-sex"
            value={form.sex}
            onChange={(e) => setField("sex", e.target.value)}
          >
            {SEX_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-ethnicity">Ethnicity</FilterLabel>
          <FilterInput
            id="patient-ethnicity"
            type="text"
            maxLength={16}
            value={form.ethnicity}
            onChange={(e) => setField("ethnicity", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-country">Country</FilterLabel>
          <AdminSelectField
            id="patient-country"
            value={form.countryId}
            onChange={(e) => setField("countryId", e.target.value)}
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country.id} value={String(country.id)}>
                {country.name}
              </option>
            ))}
          </AdminSelectField>
          {errors.countryId && <FieldError>{errors.countryId}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="patient-smoking">Smoking</FilterLabel>
          <AdminSelectField
            id="patient-smoking"
            value={form.smoking}
            onChange={(e) => setField("smoking", e.target.value)}
          >
            {SMOKING_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        {/* A wrapping chip list in a normal grid column pushes the fields
            beside it far apart (EN-5150 review), so the ward set gets its own
            full-width row. */}
        <FilterGroup span="full">
          <FilterLabel>All wards</FilterLabel>
          <WardChips>
            {(patient.wards ?? []).length === 0 && (
              <ReadOnlyValue>Not assigned to any ward</ReadOnlyValue>
            )}
            {(patient.wards ?? []).map((ward) => (
              <WardChip key={ward.wardId} primary={ward.isPrimary}>
                {ward.wardName}
                {ward.isPrimary ? " (primary)" : ""}
              </WardChip>
            ))}
            {wardAction}
          </WardChips>
          <FieldHint>
            Since EN-4960 a patient can belong to several wards. Their workflows
            and alerts fire from all of them, not only the primary.
          </FieldHint>
        </FilterGroup>

        <FilterGroup span="full">
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setField("publish", e.target.checked)}
            />
            Published
          </CheckboxField>
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.archived}
              onChange={(e) => setField("archived", e.target.checked)}
            />
            Archived
          </CheckboxField>
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.rkdRegistry}
              onChange={(e) => setField("rkdRegistry", e.target.checked)}
            />
            RKD registry
          </CheckboxField>
        </FilterGroup>

        {(willUnpublish || willArchive) && (
          <FilterGroup span="full">
            <WarningNotice>
              {willArchive
                ? "Archiving ends this patient's monitoring: their workflows stop evaluating and they drop out of the clinician lists."
                : "Un-publishing removes this patient from the clinician patient lists."}
            </WarningNotice>
          </FilterGroup>
        )}
      </FormGrid>

      <SectionActions>
        <FilterButton
          variant="primary"
          disabled={isUpdatingPatient || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingPatient ? "Saving..." : "Save patient details"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
